import { capabilitiesOf, type ActorRole, type Capability } from '@dpem/contracts';

/**
 * What the signed-in user may do, derived from their roles.
 *
 * The table itself lives in `@dpem/contracts` and is pinned to the API's domain
 * `Role` value object by a spec there. Nothing in this file decides policy; it
 * reads the shared table so the UI and the server reach the same answer.
 *
 * This is presentation, not security. A control hidden here is a courtesy — the
 * endpoint's guard is the boundary, and it re-derives the same verdict on every
 * request. Anything that would be unsafe if a user un-hid a button by editing
 * the DOM is a server-side bug, not a reason to add a check here.
 */

export type { Capability };

export interface CapabilitySet {
  readonly roles: readonly ActorRole[];
  /** Holds the capability. */
  readonly can: (capability: Capability) => boolean;
  /** Holds every one — an action that needs two grants (author *and* publish). */
  readonly canAll: (...capabilities: readonly Capability[]) => boolean;
  /** Holds at least one — for a nav entry that several roles can reach. */
  readonly canAny: (...capabilities: readonly Capability[]) => boolean;
  readonly isAdmin: boolean;
}

/** The set for an anonymous visitor: nothing at all, not even read. */
export const NO_CAPABILITIES: CapabilitySet = Object.freeze({
  roles: [],
  can: () => false,
  canAll: (...capabilities: readonly Capability[]) => capabilities.length === 0,
  canAny: () => false,
  isAdmin: false,
});

export const capabilitySetFor = (roles: readonly ActorRole[]): CapabilitySet => {
  if (roles.length === 0) return NO_CAPABILITIES;

  const granted = capabilitiesOf(roles);
  const can = (capability: Capability): boolean => granted.has(capability);

  return Object.freeze({
    roles,
    can,
    canAll: (...capabilities: readonly Capability[]) => capabilities.every(can),
    canAny: (...capabilities: readonly Capability[]) => capabilities.some(can),
    isAdmin: roles.includes('ADMIN'),
  });
};

/** Role names as a person would read them, for the account menu and audit rows. */
export const ROLE_LABELS: Readonly<Record<ActorRole, string>> = Object.freeze({
  ADMIN: 'Administrator',
  METHOD_AUTHOR: 'Method author',
  REVIEWER: 'Reviewer',
  WORKSHOP_FACILITATOR: 'Workshop facilitator',
  VIEWER: 'Viewer',
});

/**
 * Each capability as the thing it lets somebody do, not as its identifier.
 *
 * The admin panel shows a role's grants before an administrator hands that role
 * out, and `workshop:publishVariant` is not a sentence anybody can act on. These
 * are verbs in the platform's own vocabulary, and the record is exhaustive by
 * type — a new capability in `@dpem/contracts` fails to compile until it has a
 * phrase here, which is the point: a grant nobody can describe is a grant
 * nobody should be making.
 */
export const CAPABILITY_LABELS: Readonly<Record<Capability, string>> = Object.freeze({
  'method:read': 'Read methods, plans and workshops',
  'method:author': 'Edit a draft method',
  'method:submit': 'Submit a draft for review',
  'method:review': 'Approve or reject a submitted method',
  'method:publish': 'Publish an approved method',
  'method:import': 'Import a method document',
  'catalog:manage': 'Maintain the catalogue of tools, assets and agents',
  'catalog:manageSession': "Maintain one adoption session's own client catalogue",
  'deliverable:manage': 'Maintain deliverable templates',
  'attachment:upload': 'Attach files and links',
  'workshop:facilitate': 'Run an adoption workshop',
  'workshop:publishVariant': 'Publish a tailored method from a workshop',
  'plan:manage': 'Generate and edit delivery plans',
  'assist:invoke': 'Ask the AI assistant for help',
  'audit:read': 'Read the audit trail',
  'admin:manage': 'Administer the platform, including roles',
});

/**
 * The role to show when a user holds several. Highest privilege wins, because
 * that is the one that explains what they can do rather than what they cannot.
 */
const ROLE_PRECEDENCE: readonly ActorRole[] = [
  'ADMIN',
  'METHOD_AUTHOR',
  'REVIEWER',
  'WORKSHOP_FACILITATOR',
  'VIEWER',
];

export const primaryRole = (roles: readonly ActorRole[]): ActorRole | null =>
  ROLE_PRECEDENCE.find((role) => roles.includes(role)) ?? null;
